<template>
    <dialog ref="modal" class="work-pdf-dialog work-pdf" aria-modal="true" :aria-label="title" @cancel.prevent="close" @keydown.stop="onKeydown" @copy="clipboardEvent($event, 'copyEvent')" @paste="clipboardEvent($event, 'pasteEvent')">
            <header class="work-pdf__header">
                <i class="mdi mdi-file-pdf-box work-pdf__icon" aria-hidden="true"></i>
                <h1 :title="title">{{ title }}</h1>
                <button ref="close" type="button" class="work-pdf__button" aria-label="Chiudi anteprima PDF" @click="close"><i class="mdi mdi-close" aria-hidden="true"></i> Chiudi</button>
            </header>
            <work-pdf-toolbar :ready="ready && !printing" :editing="editing" :allow-print="allowPrint" :allow-download="allowDownload" :context="context" :page="page" :pages="pages" :scale="scale" @command="command" @page="goToPage" />
            <work-pdf-editor v-if="active && editing && editorSession" ref="editor" :session="editorSession" :context="context" :menu-point="editorMenuPoint" :initial-state="resumeState && resumeState.editor" :allow-print="allowPrint" :allow-download="allowDownload" @command="command" @close="editing = false" @saved="saved" />
            <div class="work-pdf__body" :aria-busy="!ready && !statusError" @contextmenu="openPageMenu">
                <work-pdf-document :active="active" :generation="generation" :url="url" :prepared="prepared" :context="context" :filename="title" @open="onOpen" @error="viewerError = 'Impossibile aprire il PDF. Riprova o verifica il documento.'" />
                <div v-if="statusError || !ready || printing" class="work-pdf__status" :role="statusError ? 'alert' : 'status'">
                    <template v-if="statusError"><i class="mdi mdi-file-alert-outline" aria-hidden="true"></i><p>{{ statusError }}</p><button type="button" class="work-pdf__button" @click="$emit('retry')">Riprova</button></template>
                    <template v-else><span class="work-pdf__spinner" aria-hidden="true"></span><p>{{ printing ? printStatus : url ? 'Apertura del documento…' : 'Recupero del PDF…' }}</p></template>
                </div>
            </div>
    </dialog>
</template>

<script>
import WorkPdfToolbar from "./WorkPdfToolbar.vue";
import WorkPdfDocument from "./WorkPdfDocument.vue";
import { loadPdfEngine } from "./pdfEngine";
import { printPdf } from "./printPdf.mjs";
import { capturePdfEditorView, restorePdfEditorView } from './pdfEditorResume.mjs';
import { rememberPdfRotation } from './pdfViewPreferences.mjs';

export default {
    name: "WorkPdfDialog",
    components: { WorkPdfToolbar, WorkPdfDocument,
        WorkPdfEditor: () => import(/* webpackChunkName: "work-pdf-editor" */ "./WorkPdfEditor.vue") },
    props: { active: Boolean, generation: Number, url: String, prepared: Object, title: String, error: String, context: Object, resumeState:Object, allowPrint: { type: Boolean, default: true }, allowDownload: { type: Boolean, default: true } },
    data: () => ({ opened: false, ready: false, printing: false, printStatus: "", editing: false, editorSession: null, editorMenuPoint: null, page: 1, pages: 0, scale: 100, viewerError: "",
    }),
    computed: { statusError() { return this.error || this.viewerError; } },
    created() { this._pdfApplication = null; this._pdfListeners = []; },
    mounted() { this.opened = this.active; if (this.active) this.captureFocus(); },
    beforeDestroy() { this.releaseDocument(); this.restoreFocus(); },
    watch: {
        active(value) {
            this.opened = value;
            if (value) this.captureFocus();
            else { this.releaseDocument(); this.restoreFocus(); }
        },
        generation() { this.releaseDocument(); },
    },
    methods: {
        saved(revision){const resume=capturePdfEditorView(this._pdfApplication,this.$refs.editor);this.$emit('saved',{revision,resume});},
        openPageMenu(event) {
            const page = event.target.closest('.page');
            if (!this.context || !this.ready || this.printing || !page) return;
            event.preventDefault();
            const viewer = this._pdfApplication.pdfViewer;
            const number = Number(page.dataset.pageNumber) || viewer.currentPageNumber;
            const view = viewer.getPageView(number - 1), rect = view.div.getBoundingClientRect();
            const pdfPoint = view.viewport.convertToPdfPoint((event.clientX - rect.left) * view.viewport.width / rect.width, (event.clientY - rect.top) * view.viewport.height / rect.height);
            if (viewer.currentPageNumber !== number) viewer.currentPageNumber = number;
            this.editorMenuPoint = { pdfPoint, clientX: event.clientX, clientY: event.clientY };
            if (!this.editing) this.command("edit");
        },
        onKeydown(event) {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "s" && this.$refs.editor) {
                event.preventDefault(); this.$refs.editor.save(); return;
            }
            if ((event.ctrlKey || event.metaKey) && this.$refs.editor && !event.target.closest('input,textarea,[contenteditable="true"]')) {
                const editor=this.$refs.editor, key=event.key.toLowerCase();
                if (key === 'd') { event.preventDefault(); editor.duplicate(); return; }
                if (key === 'z') { event.preventDefault(); if (event.shiftKey) editor.redo(); else editor.undo(); return; }
                if (key === 'y') { event.preventDefault(); editor.redo(); return; }
            }
            if (event.key !== "Tab") return;
            const controls = [...this.$el.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]')]
                .filter((node) => node.getClientRects().length && getComputedStyle(node).visibility !== "hidden");
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        },
        clipboardEvent(event,method) {
            if (!event.target.closest('input,textarea,[contenteditable="true"]')) this.$refs.editor?.[method](event);
        },
        captureFocus() {
            if (typeof document === "undefined" || this._scrollLocked) return;
            this._previousOverflow = document.documentElement.style.overflow;
            document.documentElement.style.overflow = "hidden";
            this._scrollLocked = true;
            this.$nextTick(() => {
                if (!this.active || !this._scrollLocked) return;
                this.$refs.modal.showModal();
                this.$refs.close?.focus();
            });
        },
        restoreFocus() {
            if (typeof document === "undefined" || !this._scrollLocked) return;
            this.$refs.modal?.close();
            document.documentElement.style.overflow = this._previousOverflow;
            this._scrollLocked = false;
        },
        releaseDocument() {
            const eventBus = this._pdfApplication?.eventBus;
            this._pdfListeners.forEach(([name, handler]) => eventBus?.off(name, handler));
            this._pdfListeners = [];
            if (this._pdfApplication && this._originalError) this._pdfApplication.error = this._originalError;
            this._pdfApplication = null;
            this._originalError = null;
            this.ready = false;
            this.viewerError = "";
            this.pages = 0;
            this.page = 1;
            this.scale = 100;
            this._printAbort?.abort();
            this._printRelease?.();
            this._printRelease = null;
            this.printing = false;
            this.editing = false;
            this.editorSession = null;
            this.editorMenuPoint = null;
        },
        requestClose(confirmed = () => this.close()) { return this.$refs?.editor?.requestClose(confirmed) !== false; },
        close() { if (!this.requestClose()) return; this.opened = false; this.$nextTick(() => this.$emit("close")); },
        onCreated(application) {
            this._pdfApplication = application;
            const originalError = application.error;
            this._originalError = originalError;
            application.error = (...args) => {
                if (this.active) this.viewerError = "Impossibile aprire il PDF. Riprova o verifica il documento.";
                return originalError.apply(application, args);
            };
        },
        onOpen(application) {
            if (!this.active || !this.url || !application?.pdfDocument) return;
            this._pdfApplication = application;
            this.$emit("open", application);
            this.pages = application.pdfDocument.numPages;
            const listen = (name, handler) => {
                application.eventBus.on(name, handler);
                this._pdfListeners.push([name, handler]);
            };
            listen("pagechanging", ({ pageNumber }) => { this.page = application.pagePosition?.(pageNumber) || pageNumber; });
            listen("pageorderchanged", () => { this.page = application.pagePosition?.(application.pdfViewer.currentPageNumber) || 1; });
            listen("scalechanging", ({ scale }) => { this.scale = Math.round(scale * 100); });
            listen("pagerendered", this.onRendered);
            application.pdfViewer.currentScaleValue = "page-fit";
            if(this.resumeState){
                restorePdfEditorView(application,this.resumeState);
                rememberPdfRotation(this.context?.revision_id || application.pdfDocument.fingerprints[0],application.pdfViewer.pagesRotation,application.pdfViewer.currentPageNumber);
                this.editorSession=Object.freeze({app:application});this.editing=true;
                this.page=application.pdfViewer.currentPageNumber;
            }
            application.pdfCursorTools.switchTool(1);
        },
        onRendered(event) {
            // The legacy pages-rendered callback passes the application, whose
            // `error` member is a method rather than a rendering failure.
            if (event?.error && typeof event.error !== "function") {
                console.error("[PDF] Rendering fallito", event.error);
                this.viewerError = "Impossibile visualizzare il PDF. Riprova.";
                return;
            }
            if (this.ready || !this.active || !this.url || !this._pdfApplication?.pdfDocument) return;
            this.ready = true;
            if(this.resumeState)this.$nextTick(()=>{const container=this._pdfApplication?.pdfViewer.container;if(container){container.scrollTop=this.resumeState.scrollTop;container.scrollLeft=this.resumeState.scrollLeft;}});
            this.scale = Math.round(this._pdfApplication.pdfViewer.currentScale * 100);
            this.$emit("rendered");
        },
        goToPage(value) {
            const number = Number(value);
            if (!this.ready || !Number.isInteger(number) || number < 1 || number > this.pages) return;
            if (this._pdfApplication.goToPage) this._pdfApplication.goToPage(number);
            else this._pdfApplication.pdfViewer.currentPageNumber = number;
        },
        command(action) {
            const app = this._pdfApplication;
            if (!this.ready || !app) return;
            const viewer = app.pdfViewer;
            switch (action) {
                case "previous": this.goToPage(this.page - 1); break;
                case "next": this.goToPage(this.page + 1); break;
                case "zoomIn": app.zoomIn(); break;
                case "zoomOut": app.zoomOut(); break;
                case "fit": viewer.currentScaleValue = "page-fit"; break;
                case "width": viewer.currentScaleValue = "page-width"; break;
                case "rotateLeft": app.rotatePages(-90); break;
                case "rotateRight": app.rotatePages(90); break;
                case "find": app.findBar.toggle(); break;
                case "thumbnails": app.toggleThumbnails?.(); break;
                case "organizePages": app.toggleThumbnails?.(true); break;
                case "original": this.showVersion(true); break;
                case "annotated": this.showVersion(false); break;
                case "edit":
                    if (this.editing) { if (this.requestClose(() => { this.editing = false; })) this.editing = false; }
                    else { this.editorSession = Object.freeze({ app }); this.editing = true; }
                    break;
                case "download": if (this.allowDownload && this.requireSaved()) app.download(); break;
                case "print": if (this.allowPrint && this.requireSaved()) this.printDocument(); break;
            }
        },
        showVersion(original) {
            if (original === !!this.context?.original) return;
            const open = () => this.$emit("original", original);
            if (this.requestClose(open)) open();
        },
        requireSaved() {
            const editor = this.$refs?.editor;
            if (!editor?.hasChanges) return true;
            editor.error = "Premi Salva prima di scaricare o stampare le modifiche.";
            return false;
        },
        async printDocument() {
            if (this.printing) return;
            const application = this._pdfApplication;
            if (!application) return;
            const controller = this._printAbort = new AbortController();
            this.printing = true;
            this.printStatus = "Preparazione stampa…";
            try {
                const { api } = await loadPdfEngine();
                this._printRelease = await printPdf(application.pdfDocument, { api, signal: controller.signal, identity: this.context?.revision_id,
                    onStatus: (status) => { this.printStatus = status; } });
            } catch (error) {
                if (!controller.signal.aborted) this.viewerError = "Impossibile preparare la stampa. Riprova.";
            } finally { if (this._printAbort === controller) this.printing = false; }
        },
    },
};
</script>

<style>
.work-pdf-dialog { position: fixed; inset: 0; z-index: 99999; margin: 0; width: 100%; max-width: none; max-height: none; padding: 0; border: 0; }
.work-pdf-dialog:not([open]) { display: none; }
.work-pdf { --pdf-surface: var(--surface); --pdf-border: var(--border); display: flex; flex-direction: column; height: 100vh; height: 100dvh; color: var(--ink); background: var(--bg); }
.work-pdf__button { min-height: 32px; padding: 4px 12px; border: 1px solid var(--border-control); border-radius: var(--radius-sm); color: inherit; font-size: var(--text-xs); }
.work-pdf__button:hover { background: var(--surface-2); }
.work-pdf__button:focus-visible { outline: 2px solid var(--focus); outline-offset: 2px; }
.work-pdf__icon { font-size: 24px; color: var(--brand-ink); }
.work-pdf__spinner { width: 32px; height: 32px; border: 3px solid var(--border); border-top-color: var(--brand); border-radius: 50%; animation: work-pdf-spin .8s linear infinite; }
@keyframes work-pdf-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .work-pdf__spinner { animation: none; } }
.work-pdf__header { display: flex; align-items: center; flex: 0 0 auto; gap: 12px; padding: 8px 16px; background: var(--pdf-surface); border-bottom: 1px solid var(--pdf-border); }
.work-pdf__header h1 { flex: 1; min-width: 0; overflow: hidden; margin: 0; font-size: 16px; font-weight: 500; white-space: nowrap; text-overflow: ellipsis; }
.work-pdf__body { position: relative; flex: 1 1 auto; min-height: 0; }
.work-pdf__status { position: absolute; inset: 0; z-index: 30; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; padding: 24px; text-align: center; background: var(--bg); }
.work-pdf .v-btn { color: inherit; }
@media (max-width: 600px) { .work-pdf__header { gap: 8px; padding: 8px; } .work-pdf__header h1 { font-size: 14px; } }
</style>
